/**
 * Client Dashboard (renamed from Weekly Management per CORE-D-01) —
 * cross-client fleet triage board.
 *
 * One row per ENROLLED tenant (GET /google-ads/weekly/fleet): its latest run's
 * status, per-engine flag counts, $-at-stake, monitor-step health, and pending
 * approvals. Defaults to "needs attention" first (most $ at stake), but every
 * column is sortable, resizable, and filterable, and the table paginates —
 * matching the house data-table toolkit used across the webapp.
 *
 * Always on (no feature flag, per Carl) — view-only + operator-gated. Extracted
 * component (not in the GoogleAdsOpsPage monolith), per the weekly-management-ux-spec.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  FilterBuilder,
  SortableTh,
  TablePagination,
  applyFilterRules,
  useColumnResize,
  usePagination,
  useTableSort,
  type FilterColumn,
  type FilterConjunction,
  type FilterRule,
  type SortAccessors,
} from '@/components/ui/data-table';
import {
  listFleet,
  type FleetRunSummary,
  type FleetTenant,
} from '@/services/googleAdsWeeklyService';
import {
  ClientSearchBox,
  ClientSearchEmpty,
  filterClientsByQuery,
  useClientNamesByWebsite,
  useClientSearchParam,
} from '@/features/ads/client-dashboard/ClientSearch';

function dollarsAtStake(run: FleetRunSummary): number {
  return (run.wasted_spend_usd || 0) + (run.projected_underspend_usd || 0);
}

function money(n: number): string {
  if (!n) return '$0';
  return `$${Math.round(n).toLocaleString()}`;
}

function statusClass(status: string): string {
  switch (status) {
    case 'success':
      return 'text-teal-dark';
    case 'partial':
      return 'text-coral-dark';
    case 'failed':
      return 'text-destructive';
    default:
      return 'text-muted-foreground';
  }
}

const MONITOR_KEYS = ['budget_pacing', 'kpi_drift', 'kpi_plan'] as const;

function monitorFailedCount(run: FleetRunSummary): number | null {
  const rs = run.review_steps || {};
  const present = MONITOR_KEYS.filter((k) => k in rs);
  if (present.length === 0) return null;
  return present.filter((k) => rs[k] === false).length;
}

function MonitorHealth({ run }: { run: FleetRunSummary }) {
  const failed = monitorFailedCount(run);
  if (failed === null) {
    return <span className="text-xs text-muted-foreground">not run</span>;
  }
  if (failed > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-destructive">
        <AlertCircle className="h-3 w-3" /> {failed} failed
      </span>
    );
  }
  return <span className="text-xs text-teal-dark">✓ ran</span>;
}

// --- table config -----------------------------------------------------------
// Accessors read the latest run; tenants with no run yield null so they sink to
// the bottom regardless of sort direction (matching the old needs-attention sort).
const SORT_ACCESSORS: SortAccessors<FleetTenant> = {
  client: (t) => t.tenant_id.toLowerCase(),
  mode: (t) => t.enrollment_mode,
  status: (t) => t.latest_run?.status ?? null,
  terms: (t) => t.latest_run?.search_terms_flagged ?? null,
  ads: (t) => t.latest_run?.ads_paused ?? null,
  kw: (t) => t.latest_run?.keywords_paused ?? null,
  stake: (t) => (t.latest_run ? dollarsAtStake(t.latest_run) : null),
  monitors: (t) => (t.latest_run ? monitorFailedCount(t.latest_run) : null),
  pending: (t) => t.pending_approvals,
};

const RESIZE_DEFAULTS: Record<string, number> = {
  client: 200,
  mode: 92,
  status: 110,
  terms: 80,
  ads: 70,
  kw: 70,
  stake: 120,
  monitors: 120,
  pending: 92,
};

function uniqueOptions(values: (string | null | undefined)[]): { value: string; label: string }[] {
  const set = new Set<string>();
  for (const v of values) if (v) set.add(v);
  return [...set].sort().map((v) => ({ value: v, label: v }));
}

function Tile({ n, l, tone }: { n: ReactNode; l: string; tone?: 'warn' }) {
  return (
    <div className="rounded-lg border border-border bg-warm-bg p-3">
      <div className={`text-2xl font-bold tabular-nums ${tone === 'warn' ? 'text-coral-dark' : ''}`}>{n}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{l}</div>
    </div>
  );
}

export function WeeklyFleetBoard({
  onSelectClient,
}: {
  onSelectClient?: (tenantId: string) => void;
}) {
  const q = useQuery({
    queryKey: ['google-ads-weekly-fleet'],
    queryFn: () => listFleet(),
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
  });

  const rows: FleetTenant[] = useMemo(() => q.data ?? [], [q.data]);

  const { sortKey, sortDir, toggleSort, sortRows } = useTableSort<FleetTenant>('stake', 'desc');
  const { widths, startResize } = useColumnResize(RESIZE_DEFAULTS);
  const [rules, setRules] = useState<FilterRule[]>([]);
  const [conjunction, setConjunction] = useState<FilterConjunction>('and');
  // Name / website search, kept in ?q=. Applied before the filter rules and
  // sort, so all three work together.
  const [search, setSearch] = useClientSearchParam();
  const namesByWebsite = useClientNamesByWebsite();
  const searched = useMemo(
    () => filterClientsByQuery(rows, search, (t) => t.tenant_id, namesByWebsite),
    [rows, search, namesByWebsite],
  );

  const filterColumns: FilterColumn<FleetTenant>[] = useMemo(
    () => [
      { key: 'client', label: 'Client', type: 'text', accessor: (t) => t.tenant_id },
      {
        key: 'mode', label: 'Mode', type: 'enum',
        accessor: (t) => t.enrollment_mode,
        options: uniqueOptions(rows.map((t) => t.enrollment_mode)),
      },
      {
        key: 'status', label: 'Last run', type: 'enum',
        accessor: (t) => t.latest_run?.status ?? 'no runs',
        options: uniqueOptions(rows.map((t) => t.latest_run?.status ?? 'no runs')),
      },
      { key: 'terms', label: 'Terms flagged', type: 'number', accessor: (t) => t.latest_run?.search_terms_flagged ?? null },
      { key: 'ads', label: 'Ads paused', type: 'number', accessor: (t) => t.latest_run?.ads_paused ?? null },
      { key: 'kw', label: 'Keywords paused', type: 'number', accessor: (t) => t.latest_run?.keywords_paused ?? null },
      { key: 'stake', label: '$ at stake', type: 'number', accessor: (t) => (t.latest_run ? dollarsAtStake(t.latest_run) : null) },
      { key: 'pending', label: 'Pending approvals', type: 'number', accessor: (t) => t.pending_approvals },
    ],
    [rows],
  );

  const filteredSorted = useMemo(() => {
    const filtered = applyFilterRules(searched, rules, filterColumns, conjunction);
    return sortRows(filtered, SORT_ACCESSORS);
  }, [searched, rules, filterColumns, conjunction, sortRows]);

  const { page, pageSize, setPage, setPageSize, pageCount, slice } = usePagination(
    filteredSorted.length,
    { defaultPageSize: 20 },
  );
  const pageRows = slice(filteredSorted);

  const th = (key: string, label: string, className?: string) => (
    <SortableTh
      sortKey={key}
      currentKey={sortKey}
      dir={sortDir}
      onSort={toggleSort}
      resizable
      width={widths[key]}
      onResize={startResize}
      className={className}
    >
      {label}
    </SortableTh>
  );

  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 rounded border border-border bg-warm-bg p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading fleet…
      </div>
    );
  }

  if (q.isError) {
    return (
      <div className="rounded border border-destructive/30 bg-destructive/5 p-4 text-sm">
        <div className="font-medium text-destructive">Fleet failed to load</div>
        <p className="mt-1 text-xs text-muted-foreground">
          The fleet endpoint returned an error (503 if the Client Dashboard is
          disabled on this deploy).
        </p>
        <Button variant="outline" size="sm" className="mt-2" onClick={() => q.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded border border-dashed border-border p-4 text-sm text-muted-foreground">
        No clients are enrolled in the Client Dashboard yet. Set a client to
        warn / soft_auto / hard_auto to populate the fleet.
      </div>
    );
  }

  // Summary strip reflects the FULL fleet (not the current filter/page).
  const totalStake = rows.reduce(
    (sum, r) => sum + (r.latest_run ? dollarsAtStake(r.latest_run) : 0),
    0,
  );
  const totalPending = rows.reduce((s, r) => s + r.pending_approvals, 0);
  const withIssues = rows.filter(
    (r) => r.latest_run && dollarsAtStake(r.latest_run) > 0,
  ).length;

  // Top clients by $ at stake — a compact bar viz on the landing.
  const topClients = rows
    .filter((r) => r.latest_run && dollarsAtStake(r.latest_run) > 0)
    .sort((a, b) => dollarsAtStake(b.latest_run!) - dollarsAtStake(a.latest_run!))
    .slice(0, 6);
  const topMax = topClients.length ? dollarsAtStake(topClients[0].latest_run!) : 1;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile n={rows.length} l="Enrolled clients" />
        <Tile n={withIssues} l="With $ at stake" tone={withIssues ? 'warn' : undefined} />
        <Tile n={money(totalStake)} l="Total at stake" tone={totalStake ? 'warn' : undefined} />
        <Tile n={totalPending} l="Pending approvals" tone={totalPending ? 'warn' : undefined} />
      </div>

      {topClients.length > 0 && (
        <div className="rounded-lg border border-border bg-warm-bg p-4">
          <div className="mb-3 text-sm font-medium">Top clients by $ at stake</div>
          <div className="space-y-2">
            {topClients.map((t) => {
              const v = dollarsAtStake(t.latest_run!);
              const row = (
                <>
                  <span className="truncate text-xs font-medium">{t.tenant_id}</span>
                  <span className="h-2 rounded bg-muted">
                    <span
                      className="block h-full rounded bg-coral"
                      style={{ width: `${Math.max(4, (v / topMax) * 100)}%` }}
                    />
                  </span>
                  <span className="text-right text-xs tabular-nums text-muted-foreground">{money(v)}</span>
                </>
              );
              const cls = 'grid w-full grid-cols-[minmax(110px,1fr)_2fr_auto] items-center gap-3';
              return onSelectClient ? (
                <button
                  key={t.tenant_id}
                  onClick={() => onSelectClient(t.tenant_id)}
                  className={`${cls} rounded text-left hover:opacity-80`}
                  title="Open this client"
                >
                  {row}
                </button>
              ) : (
                <div key={t.tenant_id} className={cls}>{row}</div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <ClientSearchBox value={search} onChange={setSearch} />
        <FilterBuilder
          columns={filterColumns}
          rules={rules}
          conjunction={conjunction}
          onRulesChange={setRules}
          onConjunctionChange={setConjunction}
        />
      </div>

      {searched.length === 0 ? (
        <ClientSearchEmpty query={search} onClear={() => setSearch('')} />
      ) : (
      <>
      <div className="overflow-x-auto rounded border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              {th('client', 'Client')}
              {th('mode', 'Mode')}
              {th('status', 'Last run')}
              {th('terms', 'Terms flagged', 'text-right')}
              {th('ads', 'Ads paused', 'text-right')}
              {th('kw', 'Kw paused', 'text-right')}
              {th('stake', '$ at stake', 'text-right')}
              {th('monitors', 'Monitors')}
              {th('pending', 'Pending', 'text-right')}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map((t) => {
              const run = t.latest_run;
              return (
                <TableRow
                  key={t.tenant_id}
                  className={onSelectClient ? 'cursor-pointer' : undefined}
                  onClick={onSelectClient ? () => onSelectClient(t.tenant_id) : undefined}
                  title={onSelectClient ? 'Open this client' : undefined}
                >
                  <TableCell className="whitespace-nowrap font-medium">{t.tenant_id}</TableCell>
                  <TableCell className="text-xs uppercase text-muted-foreground">
                    {t.enrollment_mode}
                  </TableCell>
                  <TableCell className="text-xs">
                    {run ? (
                      <span className={statusClass(run.status)}>{run.status}</span>
                    ) : (
                      <span className="text-muted-foreground">no runs yet</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {run ? run.search_terms_flagged : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {run ? run.ads_paused : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {run ? run.keywords_paused : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {run ? (
                      <span
                        title={`$${Math.round(run.wasted_spend_usd)} wasted + $${Math.round(
                          run.projected_underspend_usd,
                        )} projected underspend`}
                      >
                        {run.dollars_incomplete && dollarsAtStake(run) > 0 ? '~' : ''}
                        {money(dollarsAtStake(run))}
                      </span>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    {run ? (
                      <MonitorHealth run={run} />
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {t.pending_approvals > 0 ? (
                      <span className="rounded bg-coral-light/40 px-1.5 py-0.5 text-xs font-medium text-coral-dark">
                        {t.pending_approvals}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        totalRows={filteredSorted.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
      </>
      )}
    </div>
  );
}
